import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  blockedRoles?: string[];
  _companyId?: string; // Optional company context for role checking
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [], 
  blockedRoles = [],
  _companyId 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Helper function to check if user has a role
  const hasRole = (role: string): boolean => {
    // System admin has access to everything
    if (user.is_system_admin) {
      return true;
    }

    // Check if allowed role is system_admin (blocked roles)
    if (role === 'system_admin') {
      return false;
    }

    // For company-specific roles, check across all companies
    if (user.userCompanies && Array.isArray(user.userCompanies)) {
      return user.userCompanies.some(uc => 
        uc.userRoles && 
        Array.isArray(uc.userRoles) && 
        uc.userRoles.some(ur => ur.role === role && ur.is_active)
      );
    }

    return false;
  };

  // Check if user has any of the blocked roles
  if (blockedRoles.length > 0) {
    const hasBlockedRole = blockedRoles.some(role => hasRole(role));
    if (hasBlockedRole) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <AlertTitle>Access Denied</AlertTitle>
            You do not have permission to access this page.
          </Alert>
        </Box>
      );
    }
  }

  // Check if user has any of the allowed roles (if specified)
  if (allowedRoles.length > 0) {
    const hasAllowedRole = allowedRoles.some(role => hasRole(role));
    if (!hasAllowedRole) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <AlertTitle>Access Denied</AlertTitle>
            You do not have permission to access this page.
          </Alert>
        </Box>
      );
    }
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
